import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import { Table, Button, Dropdown, Modal, Icon, Grid } from "semantic-ui-react";

import moment from "moment";

import DateTextBox from "../common/DateTextBox";
import TimeTextBox from "../common/TimeTextBox";
import formatComma from "react-lib/utils/formatComma";

const ICON = {
  IconUpload: "/static/images/icon_upload_payment.png",
};

export const COLOR = {
  primary: "var(--primary-theme-color)",
  light_pink: "#ffd8d8",
  red: "#d31919",
  green: "#06813d",
  dark_green: "#28922c",
  dark_blue: "#0c5bcc",
  light_orange: "#ffd7af",
  orange: "#d77b24",
  light_green: "#c4eac5",
  gray: "#828282",
};

export const STATUS_OPTIONS = [
  { key: 3, value: "CONFIRM", text: "ชำระแล้ว" },
  { key: 5, value: "REJECT", text: "หลักฐานมีปัญหา" },
  { key: 9, value: "REFUND", text: "คืนเงินผู้ป่วยบางส่วน" },
];

export const REFUND_STATUS_OPTIONS = [
  { key: 7, value: "ACCEPT_REQUEST_REFUND", text: "ยอมรับคำขอคืนเงิน" },
  { key: 9, value: "REFUND", text: "คืนเงินสำเร็จ" },
  { key: 8, value: "REJECT_REQUEST_REFUND", text: "ปฏิเสธคำขอคืนเงิน" },
];

const boxStyle: React.CSSProperties = {
  backgroundColor: "#d9d9d9",
  borderRadius: "4px",
  padding: "0.3rem 1rem",
  display: "inline-block",
  width: "600px",
  wordBreak: "break-word",
  whiteSpace: "normal",
  fontSize: "20px",
  textAlign: "right",
};

const labelStyle: React.CSSProperties = {
  display: "inline-block",
  width: "700px",
  fontWeight: "bold",
  verticalAlign: "top",
  fontSize: "20px",
};

const fieldContainerStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  marginBottom: "1rem",
};

export interface PaymentSlipTableProps {
  data: any[];
  emptyRows: any[];
  status?:
    | "REQUESTED"
    | "REQUESTED_CONFIRM"
    | "CONFIRMED"
    | "REJECTED"
    | "REQUESTED_REFUND"
    | "REFUNDING"
    | "REFUNDED";
  buttonLoadCheck?: any;
  onEvent?: any;
  appointmentPaymentRelationList?: any[];
  userDetail?: any;
  isPaymentRelation?: boolean;
}

const PaymentSlipTable = forwardRef((props: PaymentSlipTableProps, ref: React.Ref<unknown>) => {
  const [userDetailsMap, setUserDetailsMap] = useState<{ [key: number]: any }>({});
  const [openSlipPreview, setOpenSlipPreview] = useState(false);
  const [selectedSlipUrl, setSelectedSlipUrl] = useState("");
  const [zoom, setZoom] = useState(1);
  const [selectedPayment, setSelectedPayment] = useState<any>(null);
  const [openSlipDetail, setOpenSlipDetail] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [rejectReasonText, setRejectReasonText] = useState<string>("");
  const [openRefundDetail, setOpenRefundDetail] = useState(false);
  const [selectedRefundStatus, setSelectedRefundStatus] = useState<string | null>(null);
  const [refundDate, setRefundDate] = useState("");
  const [refundTime, setRefundTime] = useState("");
  const [columnWidths, setColumnWidths] = useState<{ [key: string]: number }>({});
  const [resizing, setResizing] = useState<{
    columnKey: string | null;
    startX: number;
    startWidth: number;
  }>({
    columnKey: null,
    startX: 0,
    startWidth: 0,
  });

  const fetchedUserIdsRef = useRef<Set<number | string>>(new Set());

  const isRequested = props.status === "REQUESTED";
  const isConfirm = props.status === "REQUESTED_CONFIRM";
  const isNotChecked = isRequested || isConfirm;
  const isRefunding =
    props.status === "REQUESTED_REFUND" ||
    props.status === "REFUNDING" ||
    selectedPayment?.patient_appointment?.status === 5;
  const addAction = isConfirm || isRefunding;
  const isRefunded = props.status === "REFUNDED";

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!resizing.columnKey) return;

      e.preventDefault();

      const deltaX = e.clientX - resizing.startX;
      const newWidth = Math.max(60, resizing.startWidth + deltaX);

      setColumnWidths((prev) => ({
        ...prev,
        [resizing.columnKey!]: newWidth,
      }));
    };

    const handleMouseUp = () => {
      setResizing({ columnKey: null, startX: 0, startWidth: 0 });
      document.body.style.cursor = "default";
      document.body.style.userSelect = "auto";
    };

    if (resizing.columnKey) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "default";
      document.body.style.userSelect = "auto";
    };
  }, [resizing]);

  useEffect(() => {
    if (props.appointmentPaymentRelationList && props.appointmentPaymentRelationList?.length > 0) {
      const uniqueEditUserIds = [
        ...new Set(
          props.appointmentPaymentRelationList
            .map((p: any) => p?.edit_user)
            .filter((id: any) => id !== null && id !== undefined),
        ),
      ];

      if (uniqueEditUserIds.length === 0) {
        return;
      }

      const missingUserIds = uniqueEditUserIds.filter(
        (id) => !userDetailsMap[id] && !fetchedUserIdsRef.current.has(id),
      );

      if (missingUserIds.length === 0) {
        return;
      }

      missingUserIds.forEach((userId) => {
        if (typeof userId === "number" || typeof userId === "string") {
          fetchedUserIdsRef.current.add(userId);
          props.onEvent({
            message: "HandleGetUserDetail",
            params: { pk: userId },
          });
        } else {
          console.error("Invalid userId type detected:", userId);
        }
      });
    }
  }, [props.appointmentPaymentRelationList, userDetailsMap]);

  useEffect(() => {
    if (props.userDetail && props.userDetail.id) {
      setUserDetailsMap((prev) => ({
        ...prev,
        [props.userDetail.id]: props.userDetail,
      }));
    }
  }, [props.userDetail]);

  useEffect(() => {
    if (props.buttonLoadCheck?.postPaymentAction === "SUCCESS") {
      setOpenSlipDetail(false);
      setSelectedStatus("");
      setRejectReasonText("");

      setOpenRefundDetail(false);
      setSelectedRefundStatus("");
      setRefundDate("");
      setRefundTime("");
    }
  }, [props.buttonLoadCheck?.postPaymentAction]);

  useImperativeHandle(ref, () => ({
    resetColumnWidths: () => {
      setColumnWidths({});
    },
  }));

  const handleZoomIn = () => setZoom((z) => Math.min(z + 0.2, 3));
  const handleZoomOut = () => setZoom((z) => Math.max(z - 0.2, 0.5));

  const handleMouseDown = (e: React.MouseEvent, columnKey: string) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth =
      columnWidths[columnKey] ||
      e.currentTarget.parentElement?.getBoundingClientRect().width ||
      150;

    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    setResizing({ columnKey, startX, startWidth });
  };

  const formattedData = useMemo(
    () =>
      props.data.map((payment: any) => {
        const user = userDetailsMap[payment.patient_appointment?.edit_user];
        const fileUrl = payment.payment_confirmation ?? null;
        const fileName = fileUrl ? fileUrl.split("/").pop().split("?")[0] : null;
        const servicePrice = payment.patient_appointment?.extra?.service?.price;

        return {
          ...payment,
          patient_full_name:
            `${payment.patient_appointment?.patient_first_name ?? ""} ${payment.patient_appointment?.patient_last_name ?? ""}`.trim(),
          patient_tel: payment.patient_appointment?.patient_tel ?? "-",
          email: user?.email ?? "-",
          address_name: payment.patient_appointment?.extra?.address?.name ?? "-",
          created_formatted: moment(payment.created).format("DD/MM/YYYY \\[HH:mm\\]"),
          due_date_formatted: moment(payment.created)
            .add(1, "day")
            .format("DD/MM/YYYY \\[HH:mm\\]"),
          service_name: payment.patient_appointment?.extra?.service?.name ?? "-",
          service_price_formatted: formatComma(servicePrice) || "-",
          patient_paid_at_formatted: payment.patient_paid_at
            ? moment(payment.patient_paid_at).format("DD/MM/YYYY \\[HH:mm\\]")
            : "-",
          fileUrl,
          fileName,
          edited_formatted: payment.edited
            ? moment(payment.edited).format("DD/MM/YYYY \\[HH:mm\\]")
            : "-",
          updated_formatted: payment.patient_appointment?.updated
            ? moment(payment.patient_appointment.updated).format("DD/MM/YYYY \\[HH:mm\\]")
            : "-",
          status_note: payment.patient_appointment?.status_note || "-",
          remark: payment.remark || "-",
          appointment_status: payment.patient_appointment?.status,
          ...(props.isPaymentRelation && {
            patient_full_name: payment.patient_name || "-",
            patient_tel: payment.patient_phone_no || "-",
            email: payment.patient_email || "-",
            address_name: payment.service_address || "-",
            service_name: payment.service_name || "-",
            service_price_formatted: formatComma(payment.service_price) || "-",
          }),
        };
      }),
    [props.data, userDetailsMap],
  );

  const ResizableHeaderCell: React.FC<{ columnKey: string; children: React.ReactNode }> = ({
    columnKey,
    children,
  }) => {
    return (
      <Table.HeaderCell
        style={{
          width: columnWidths[columnKey] || "auto",
          position: "relative",
          userSelect: "none",
          whiteSpace: "nowrap",
          height: "50px",
        }}
      >
        {children}
        <div
          onMouseDown={(e) => handleMouseDown(e, columnKey)}
          style={{
            position: "absolute",
            right: 0,
            top: 0,
            height: "50px",
            width: "6px",
            cursor: "col-resize",
            zIndex: 10,
            userSelect: "none",
            background: "transparent",
          }}
          onClick={(e) => e.stopPropagation()}
        />
      </Table.HeaderCell>
    );
  };

  return (
    <div
      style={{
        overflowX: "auto",
        width: "100%",
        display: "block",
      }}
    >
      <Table
        celled
        structured
        selectable
        style={{
          textAlign: "center",
          width: "max-content",
          minWidth: "100%",
          tableLayout: "fixed",
        }}
      >
        <Table.Header>
          <Table.Row>
            <ResizableHeaderCell columnKey="name">ชื่อผู้รับบริการ</ResizableHeaderCell>
            <ResizableHeaderCell columnKey="phone">เบอร์โทรติดต่อ</ResizableHeaderCell>
            <ResizableHeaderCell columnKey="email">อีเมล์</ResizableHeaderCell>
            <ResizableHeaderCell columnKey="address">สถานที่รับบริการ</ResizableHeaderCell>
            <ResizableHeaderCell columnKey="created">วันที่ทำรายการนัดหมาย</ResizableHeaderCell>
            <ResizableHeaderCell columnKey="dueDate">วันที่และเวลาครบกำหนดชำระ</ResizableHeaderCell>
            <ResizableHeaderCell columnKey="service">ชื่อบริการ</ResizableHeaderCell>
            <ResizableHeaderCell columnKey="price">ราคา</ResizableHeaderCell>
            {!isRequested && (
              <ResizableHeaderCell columnKey="paid">
                วันที่และเวลาที่ผู้รับบริการโอน
              </ResizableHeaderCell>
            )}
            {!isRequested && (
              <ResizableHeaderCell columnKey="slip">หลักฐานการชำระเงิน</ResizableHeaderCell>
            )}
            {!isRequested && (
              <ResizableHeaderCell columnKey="status">สถานะการตรวจสอบ</ResizableHeaderCell>
            )}
            {!isNotChecked && (
              <ResizableHeaderCell columnKey="checked">วันที่ตรวจสอบหลักฐาน</ResizableHeaderCell>
            )}
            {props.status === "REJECTED" && (
              <ResizableHeaderCell columnKey="reason">สาเหตุ</ResizableHeaderCell>
            )}
            {(isRefunding || isRefunded) && (
              <>
                <ResizableHeaderCell columnKey="requestRefund">วันที่ขอคืนเงิน</ResizableHeaderCell>
                <ResizableHeaderCell columnKey="cancelReason">
                  สาเหตุการยกเลิกทำรายการ
                </ResizableHeaderCell>
              </>
            )}
            {isRefunded && (
              <>
                <ResizableHeaderCell columnKey="refundDate">วันที่โอนเงินคืน</ResizableHeaderCell>
                <ResizableHeaderCell columnKey="refundSlip">หลักฐานการคืนเงิน</ResizableHeaderCell>
              </>
            )}
            {addAction && <ResizableHeaderCell columnKey="action">action</ResizableHeaderCell>}
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {formattedData.map((payment: any) => {
            return (
              <Table.Row key={payment.id}>
                <Table.Cell style={{ textAlign: "left" }}>{payment.patient_full_name}</Table.Cell>
                <Table.Cell style={{ textAlign: "left" }}>{payment.patient_tel}</Table.Cell>
                <Table.Cell
                  style={{
                    textAlign: "left",
                    wordBreak: "break-all",
                    whiteSpace: "normal",
                    overflowWrap: "anywhere",
                  }}
                >
                  {payment.email}
                </Table.Cell>
                <Table.Cell style={{ textAlign: "left" }}>{payment.address_name}</Table.Cell>
                <Table.Cell>{payment.created_formatted}</Table.Cell>
                <Table.Cell style={{ textAlign: "left" }}>{payment.due_date_formatted}</Table.Cell>
                <Table.Cell style={{ textAlign: "left" }}>{payment.service_name}</Table.Cell>
                <Table.Cell style={{ textAlign: "right" }}>
                  {payment.service_price_formatted}
                </Table.Cell>

                {!isRequested && (
                  <>
                    <Table.Cell>{payment.patient_paid_at_formatted}</Table.Cell>
                    <Table.Cell style={{ textAlign: "left" }}>
                      {payment.fileUrl ? (
                        <span
                          onClick={() => {
                            setSelectedSlipUrl(payment.fileUrl);
                            setOpenSlipPreview(true);
                          }}
                          style={{
                            color: COLOR.dark_blue,
                            cursor: "pointer",
                            wordBreak: "break-all",
                            whiteSpace: "normal",
                            display: "inline-block",
                            overflowWrap: "anywhere",
                          }}
                        >
                          {payment.fileName}
                        </span>
                      ) : (
                        "-"
                      )}
                    </Table.Cell>
                    <Table.Cell textAlign="center">
                      {payment.appointment_status === 5 &&
                      payment.status !== "REFUNDING" &&
                      payment.status !== "REFUNDED" ? (
                        <span
                          style={{
                            backgroundColor: "#aaeffb",
                            color: "#0c58cc",
                            padding: "4px 10px",
                            borderRadius: "8px",
                            display: "inline-block",
                            fontWeight: 500,
                          }}
                        >
                          รอคืนเงิน
                        </span>
                      ) : payment.status === "REFUNDING" ||
                        payment.status === "REQUESTED_REFUND" ? (
                        <span
                          style={{
                            backgroundColor: COLOR.light_orange,
                            color: "#d06500",
                            padding: "4px 10px",
                            borderRadius: "8px",
                            display: "inline-block",
                            fontWeight: 500,
                          }}
                        >
                          รับคำขอคืนเงิน
                        </span>
                      ) : payment.status === "REQUESTED_CONFIRM" ? (
                        <span
                          style={{
                            backgroundColor: COLOR.light_orange,
                            color: COLOR.orange,
                            padding: "4px 10px",
                            borderRadius: "8px",
                            display: "inline-block",
                            fontWeight: 500,
                          }}
                        >
                          แจ้งชำระ
                        </span>
                      ) : payment.status === "CONFIRMED" ? (
                        <span
                          style={{
                            backgroundColor: COLOR.light_green,
                            color: COLOR.green,
                            padding: "4px 10px",
                            borderRadius: "8px",
                            display: "inline-block",
                            fontWeight: 500,
                          }}
                        >
                          ชำระแล้ว
                        </span>
                      ) : payment.status === "REJECTED" ? (
                        <span
                          style={{
                            backgroundColor: "#ffd7d7",
                            color: "#ff0000",
                            padding: "4px 10px",
                            borderRadius: "8px",
                            display: "inline-block",
                            fontWeight: 500,
                          }}
                        >
                          หลักฐานมีปัญหา
                        </span>
                      ) : payment.status === "REFUNDED" ? (
                        <span
                          style={{
                            backgroundColor: COLOR.light_green,
                            color: COLOR.green,
                            padding: "4px 10px",
                            borderRadius: "8px",
                            display: "inline-block",
                            fontWeight: 500,
                          }}
                        >
                          คืนเงินสำเร็จ
                        </span>
                      ) : (
                        "-"
                      )}
                    </Table.Cell>
                  </>
                )}
                {!isNotChecked && <Table.Cell>{payment.edited_formatted}</Table.Cell>}
                {props.status === "REJECTED" && <Table.Cell>{payment.remark}</Table.Cell>}
                {(isRefunding || isRefunded) && (
                  <>
                    <Table.Cell>{payment.updated_formatted}</Table.Cell>
                    <Table.Cell>{payment.status_note}</Table.Cell>
                  </>
                )}
                {isRefunded && (
                  <>
                    <Table.Cell>{payment.edited_formatted}</Table.Cell>
                    <Table.Cell>
                      {payment.fileUrl ? (
                        <span
                          onClick={() => {
                            setSelectedSlipUrl(payment.fileUrl);
                            setOpenSlipPreview(true);
                          }}
                          style={{ color: COLOR.dark_blue, cursor: "pointer" }}
                        >
                          {payment.fileName}
                        </span>
                      ) : (
                        "-"
                      )}
                    </Table.Cell>
                  </>
                )}
                {addAction && (
                  <Table.Cell>
                    <Icon
                      name="edit"
                      size="large"
                      style={{ color: COLOR.orange, cursor: "pointer" }}
                      onClick={() => {
                        setSelectedPayment(payment);

                        if (isRefunding) {
                          setOpenRefundDetail(true);
                        } else {
                          setOpenSlipDetail(true);
                        }
                      }}
                    />
                  </Table.Cell>
                )}
              </Table.Row>
            );
          })}

          {props.emptyRows.map((_, idx) => (
            <Table.Row key={`empty-${idx}`} style={{ height: "50px" }}>
              <Table.Cell>&nbsp;</Table.Cell>
              <Table.Cell>&nbsp;</Table.Cell>
              <Table.Cell>&nbsp;</Table.Cell>
              <Table.Cell>&nbsp;</Table.Cell>
              <Table.Cell>&nbsp;</Table.Cell>
              <Table.Cell>&nbsp;</Table.Cell>
              <Table.Cell>&nbsp;</Table.Cell>
              <Table.Cell>&nbsp;</Table.Cell>
              {(isConfirm || props.status === "CONFIRMED") && (
                <>
                  <Table.Cell>&nbsp;</Table.Cell>
                  <Table.Cell>&nbsp;</Table.Cell>
                  <Table.Cell>&nbsp;</Table.Cell>
                  <Table.Cell>&nbsp;</Table.Cell>
                </>
              )}
              {props.status === "REJECTED" && (
                <>
                  <Table.Cell>&nbsp;</Table.Cell>
                  <Table.Cell>&nbsp;</Table.Cell>
                  <Table.Cell>&nbsp;</Table.Cell>
                  <Table.Cell>&nbsp;</Table.Cell>
                  <Table.Cell>&nbsp;</Table.Cell>
                </>
              )}
              {isRefunding && (
                <>
                  <Table.Cell>&nbsp;</Table.Cell>
                  <Table.Cell>&nbsp;</Table.Cell>
                  <Table.Cell>&nbsp;</Table.Cell>
                  <Table.Cell>&nbsp;</Table.Cell>
                  <Table.Cell>&nbsp;</Table.Cell>
                  <Table.Cell>&nbsp;</Table.Cell>
                  <Table.Cell>&nbsp;</Table.Cell>
                </>
              )}
              {props.status === "REFUNDED" && (
                <>
                  <Table.Cell>&nbsp;</Table.Cell>
                  <Table.Cell>&nbsp;</Table.Cell>
                  <Table.Cell>&nbsp;</Table.Cell>
                  <Table.Cell>&nbsp;</Table.Cell>
                  <Table.Cell>&nbsp;</Table.Cell>
                  <Table.Cell>&nbsp;</Table.Cell>
                  <Table.Cell>&nbsp;</Table.Cell>
                  <Table.Cell>&nbsp;</Table.Cell>
                </>
              )}
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
      <Modal
        open={openSlipPreview}
        className="card-slip-membership-modal"
        onClose={() => {
          setZoom(1);
          setOpenSlipPreview(false);
        }}
        basic
        closeOnDimmerClick
        size="fullscreen"
        mountNode={document.body}
        style={{ padding: 0, margin: 0 }}
      >
        <Icon
          name="close"
          size="large"
          color="black"
          circular
          style={{
            position: "absolute",
            top: "20px",
            left: "25px",
            cursor: "pointer",
            zIndex: 10,
            backgroundColor: "white",
          }}
          onClick={() => {
            setZoom(1);
            setOpenSlipPreview(false);
          }}
        />

        <div
          style={{
            position: "absolute",
            top: "20px",
            right: "25px",
            display: "flex",
            gap: "15px",
            zIndex: 10,
          }}
        >
          <Icon
            name="search plus"
            size="large"
            style={{ cursor: "pointer" }}
            onClick={handleZoomIn}
          />
          <Icon
            name="search minus"
            size="large"
            style={{ cursor: "pointer" }}
            onClick={handleZoomOut}
          />
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
            height: "100%",
            overflow: "auto",
          }}
        >
          <img
            src={selectedSlipUrl}
            alt="Payment Slip"
            style={{
              transform: `scale(${zoom})`,
              transition: "transform 0.2s ease-in-out",
              maxWidth: "90vw",
              maxHeight: "85vh",
              objectFit: "contain",
            }}
          />
        </div>
      </Modal>
      <Modal
        open={openSlipDetail}
        onClose={() => {
          setSelectedStatus("");
          setOpenSlipDetail(false);
          setSelectedPayment(null);
        }}
        size="large"
        style={{ padding: "20px" }}
      >
        <Modal.Content>
          {selectedPayment && (
            <>
              <h3 style={{ fontSize: "20px" }}>
                ตรวจสอบรายละเอียดการชำระเงิน:
                <span style={{ color: COLOR.primary, marginLeft: "10px" }}>
                  {`${selectedPayment.patient_appointment?.patient_first_name ?? ""} ${
                    selectedPayment.patient_appointment?.patient_last_name ?? ""
                  }`}
                </span>
              </h3>
              <div
                style={{
                  width: "100%",
                  height: "1px",
                  backgroundColor: COLOR.gray,
                  marginBottom: "2rem",
                }}
              ></div>
              <Grid>
                <Grid.Column
                  width={5}
                  textAlign="center"
                  style={{ display: "flex", justifyContent: "center", alignItems: "center" }}
                >
                  <div
                    style={{
                      maxWidth: "100%",
                      maxHeight: "80vh",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <img
                      src={selectedPayment.payment_confirmation}
                      alt="Payment Slip Detail"
                      style={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "contain",
                      }}
                    />
                  </div>
                </Grid.Column>

                <Grid.Column width={11}>
                  <div style={fieldContainerStyle}>
                    <span style={labelStyle}>ชื่อบริการ</span>
                    <div style={boxStyle}>
                      {selectedPayment.service_name || "-"}
                    </div>
                  </div>

                  <div style={fieldContainerStyle}>
                    <span style={labelStyle}>ยอดเงินโอน</span>
                    <div style={boxStyle}>{selectedPayment.service_price_formatted}</div>
                  </div>

                  <div style={fieldContainerStyle}>
                    <span style={labelStyle}>วันที่และเวลาที่ผู้รับบริการโอน</span>
                    <div
                      style={{
                        ...boxStyle,
                        width: "335px",
                        marginRight: "10px",
                        textAlign: "center",
                      }}
                    >
                      {selectedPayment.patient_paid_at
                        ? moment(selectedPayment.patient_paid_at).format("DD/MM/YYYY")
                        : "-"}
                    </div>
                    <div style={{ ...boxStyle, width: "220px", textAlign: "center" }}>
                      {selectedPayment.patient_paid_at
                        ? moment(selectedPayment.patient_paid_at).format("HH:mm")
                        : "-"}
                    </div>
                  </div>

                  <div style={fieldContainerStyle}>
                    <span style={labelStyle}>
                      สถานะการตรวจสอบ <span style={{ color: "red" }}>*</span>
                    </span>
                    <Dropdown
                      search
                      selection
                      placeholder="--เลือกตัวเลือก--"
                      options={STATUS_OPTIONS}
                      style={{ width: "590px" }}
                      value={selectedStatus || ""}
                      onChange={(e, { value }: any) => setSelectedStatus(value)}
                    />
                  </div>

                  {selectedStatus === "REJECT" && (
                    <div
                      key="reject-field"
                      style={{
                        ...fieldContainerStyle,
                        alignItems: "flex-start",
                      }}
                    >
                      <span style={labelStyle}>
                        สาเหตุที่มีปัญหา <span style={{ color: "red" }}>*</span>
                      </span>
                      <textarea
                        value={rejectReasonText}
                        onChange={(e) => setRejectReasonText(e.target.value)}
                        style={{
                          ...boxStyle,
                          height: "100px",
                          resize: "none",
                          textAlign: "left",
                          fontSize: "1em",
                          backgroundColor: "transparent",
                        }}
                        placeholder="กรอกสาเหตุที่มีปัญหา"
                        required
                      />
                    </div>
                  )}
                </Grid.Column>
              </Grid>
            </>
          )}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "10px",
              paddingTop: "1.5rem",
              fontSize: "20px",
            }}
          >
            <Button
              onClick={() => {
                setSelectedStatus("");
                setOpenSlipDetail(false);
                setSelectedPayment(null);
              }}
            >
              ปิดหน้าต่าง
            </Button>
            <Button
              style={{
                backgroundColor: COLOR.primary,
                color: "white",
              }}
              loading={props.buttonLoadCheck?.postPaymentAction === "LOADING"}
              onClick={() =>
                props.onEvent({
                  message: "HandlePostAppointmentPaymentRelationAction",
                  params: {
                    pk: selectedPayment.id,
                    status: selectedStatus,
                    reasonText: rejectReasonText,
                    division:
                      selectedPayment.patient_appointment?.division || selectedPayment.division_id,
                    isPaymentRelation: props.isPaymentRelation,
                  },
                })
              }
            >
              บันทึกข้อมูล
            </Button>
          </div>
        </Modal.Content>
      </Modal>
      <Modal
        open={openRefundDetail}
        onClose={() => {
          setSelectedRefundStatus("");
          setOpenRefundDetail(false);
          setSelectedPayment(null);
          setRefundDate("");
          setRefundTime("");
          setRejectReasonText("");
        }}
        size="large"
        style={{ padding: "20px" }}
      >
        <Modal.Content>
          {selectedPayment && (
            <>
              <h3 style={{ fontSize: "20px" }}>
                ตรวจสอบรายละเอียดการขอคืนเงิน:
                <span style={{ color: COLOR.primary, marginLeft: "10px" }}>
                  {`${selectedPayment.patient_appointment?.patient_first_name ?? ""} ${
                    selectedPayment.patient_appointment?.patient_last_name ?? ""
                  }`}
                </span>
              </h3>
              <div
                style={{
                  width: "100%",
                  height: "1px",
                  backgroundColor: COLOR.gray,
                  marginBottom: "2rem",
                }}
              ></div>
              <Grid>
                <Grid.Column
                  width={5}
                  textAlign="center"
                  style={{ display: "flex", justifyContent: "center", alignItems: "center" }}
                >
                  <div
                    style={{
                      minWidth: "300px",
                      minHeight: "300px",
                      maxWidth: "100%",
                      maxHeight: "80vh",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      cursor: "pointer",
                      border: "2px solid #c9c9c9",
                      borderRadius: "12px",
                      padding: "20px",
                      backgroundColor: "#fafafa",
                      transition: "border 0.2s ease",
                    }}
                    onClick={() => document.getElementById("refund-proof-upload")?.click()}
                  >
                    <input
                      id="refund-proof-upload"
                      type="file"
                      accept="image/*"
                      style={{ display: "none" }}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          const imageUrl = URL.createObjectURL(file);
                          setSelectedPayment((prev: any) => ({
                            ...prev,
                            refund_proof_preview: imageUrl,
                            refund_proof_file: file,
                          }));
                        }
                      }}
                    />

                    {selectedPayment?.refund_proof_preview ? (
                      <img
                        src={selectedPayment.refund_proof_preview}
                        alt="Refund Proof"
                        style={{
                          maxWidth: "100%",
                          maxHeight: "70vh",
                          objectFit: "contain",
                          borderRadius: "8px",
                        }}
                      />
                    ) : (
                      <>
                        <img
                          src={ICON.IconUpload}
                          alt="Upload Icon"
                          style={{
                            width: "80px",
                            height: "80px",
                            marginBottom: "10px",
                            opacity: 0.6,
                          }}
                        />
                        <span style={{ color: "#666", fontSize: "16px" }}>
                          อัปโหลดหลักฐานการคืนเงิน
                        </span>
                      </>
                    )}
                  </div>
                </Grid.Column>

                <Grid.Column width={11}>
                  <div
                    style={{
                      ...boxStyle,
                      color: "red",
                      textAlign: "left",
                      marginBottom: "2rem",
                      backgroundColor: COLOR.light_pink,
                      width: "100%",
                    }}
                  >
                    สาเหตุที่ยกเลิกนัดหมาย:{" "}
                    <span style={{ color: "black" }}>
                      {selectedPayment.patient_appointment?.status_note || "-"}
                    </span>
                  </div>
                  <div style={fieldContainerStyle}>
                    <span style={labelStyle}>ชื่อบริการ</span>
                    <div style={boxStyle}>{selectedPayment.service_name || "-"}</div>
                  </div>

                  <div style={fieldContainerStyle}>
                    <span style={labelStyle}>ยอดเงินโอน</span>
                    <div style={boxStyle}>{selectedPayment.service_price_formatted}</div>
                  </div>

                  <div style={fieldContainerStyle}>
                    <span style={labelStyle}>วันที่และเวลาที่ผู้รับบริการโอน</span>
                    <div
                      style={{
                        ...boxStyle,
                        width: "335px",
                        marginRight: "10px",
                        textAlign: "center",
                      }}
                    >
                      {selectedPayment.patient_paid_at
                        ? moment(selectedPayment.patient_paid_at).format("DD/MM/YYYY")
                        : "-"}
                    </div>
                    <div style={{ ...boxStyle, width: "220px", textAlign: "center" }}>
                      {selectedPayment.patient_paid_at
                        ? moment(selectedPayment.patient_paid_at).format("HH:mm")
                        : "-"}
                    </div>
                  </div>
                  <div style={fieldContainerStyle}>
                    <span style={labelStyle}>วันที่ขอคืนเงิน</span>
                    <div
                      style={{
                        ...boxStyle,
                        width: "335px",
                        marginRight: "10px",
                        textAlign: "center",
                      }}
                    >
                      {selectedPayment.patient_appointment?.updated
                        ? moment(selectedPayment.patient_appointment?.updated).format("DD/MM/YYYY")
                        : "-"}
                    </div>
                    <div style={{ ...boxStyle, width: "220px", textAlign: "center" }}>
                      {selectedPayment.patient_appointment?.updated
                        ? moment(selectedPayment.patient_appointment?.updated).format("HH:mm")
                        : "-"}
                    </div>
                  </div>

                  <div style={fieldContainerStyle}>
                    <span style={labelStyle}>
                      สถานะการตรวจสอบการคืนเงิน <span style={{ color: "red" }}>*</span>
                    </span>
                    <Dropdown
                      search
                      selection
                      placeholder="--เลือกตัวเลือก--"
                      options={REFUND_STATUS_OPTIONS}
                      style={{ width: "590px" }}
                      value={selectedRefundStatus || ""}
                      onChange={(e, { value }: any) => setSelectedRefundStatus(value)}
                    />
                  </div>

                  {selectedRefundStatus === "REFUND" && (
                    <div
                      key="refund-field"
                      style={{
                        ...fieldContainerStyle,
                        alignItems: "center",
                        flexDirection: "row",
                        justifyContent: "flex-start",
                        gap: "10px",
                      }}
                    >
                      <span style={{ ...labelStyle, width: "346px" }}>
                        วันที่โอนคืนผู้รับบริการ <span style={{ color: "red" }}>*</span>
                      </span>
                      <DateTextBox
                        value={refundDate}
                        onChange={(val: any) => setRefundDate(val)}
                        placeholder="เลือกวันที่โอนคืน"
                        inputStyle={{ maxWidth: "186px" }}
                      />
                      <div style={{ width: "125px", position: "relative" }}>
                        <style>
                          {`
                            .ui.search.selection.dropdown.icon {
                              width: 100% !important;
                              min-width: unset !important;
                              max-width: none !important;
                            }
                            .ui.search.selection.dropdown.icon > input.search {
                              width: 100% !important;
                              max-width: none !important;
                            }
                          `}
                        </style>
                        <TimeTextBox
                          value={refundTime || "00:00"}
                          onTimeChange={((_: any, val: string) => setRefundTime(val)) as any}
                          style={{ width: "100%" }}
                          interval={1}
                          minHour={0}
                          maxHour={23}
                          useDefaultTime={true}
                          clearable
                          search
                        />
                      </div>
                    </div>
                  )}
                  {selectedRefundStatus === "REJECT_REQUEST_REFUND" && (
                    <div
                      key="reject-field"
                      style={{
                        ...fieldContainerStyle,
                        alignItems: "flex-start",
                      }}
                    >
                      <span style={labelStyle}>
                        สาเหตุที่ปฏิเสธ <span style={{ color: "red" }}>*</span>
                      </span>
                      <textarea
                        value={rejectReasonText}
                        onChange={(e) => setRejectReasonText(e.target.value)}
                        style={{
                          ...boxStyle,
                          height: "100px",
                          resize: "none",
                          textAlign: "left",
                          fontSize: "1em",
                          backgroundColor: "transparent",
                        }}
                        placeholder="กรอกสาเหตุที่ปฏิเสธ"
                        required
                      />
                    </div>
                  )}
                </Grid.Column>
              </Grid>
            </>
          )}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "10px",
              paddingTop: "1.5rem",
              fontSize: "20px",
            }}
          >
            <Button
              onClick={() => {
                setSelectedRefundStatus("");
                setOpenRefundDetail(false);
                setSelectedPayment(null);
                setRefundDate("");
                setRefundTime("");
                setRejectReasonText("");
              }}
            >
              ปิดหน้าต่าง
            </Button>
            <Button
              style={{
                backgroundColor: COLOR.primary,
                color: "white",
              }}
              loading={props.buttonLoadCheck?.postPaymentAction === "LOADING"}
              onClick={() =>
                props.onEvent({
                  message: "HandlePostAppointmentPaymentRelationAction",
                  params: {
                    pk: selectedPayment.id,
                    status: selectedRefundStatus,
                    reasonText: rejectReasonText,
                    division:
                      selectedPayment.patient_appointment?.division || selectedPayment.division_id,
                  },
                })
              }
            >
              บันทึกข้อมูล
            </Button>
          </div>
        </Modal.Content>
      </Modal>
    </div>
  );
});

export default PaymentSlipTable;
